import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  launchGame,
  launchSports,
  resetLaunch,
} from "../../features/slices/GamesSlice";
import loadingGif from "../../assets/images/loading.gif";

/**
 * Game launch page.
 * Route: /play-game/:prdId (optional ?table=<table_id>&name=<game name>)
 * POSTs to /games/launch and embeds the returned launch_url in a
 * viewport-filling iframe.
 * With `sports` (route /sports) it POSTs to /sports/launch instead.
 */
export default function PlayGame({ sports = false }) {
  const { id: prdId } = useParams();
  const [searchParams] = useSearchParams();
  const tableId = searchParams.get("table") || "";
  const gameId = searchParams.get("game") || "";
  const category = searchParams.get("category") || "";
  const gameName = sports ? "Sports" : searchParams.get("name") || "";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const user = useSelector((state) => state.user);
  const { launchUrl, launchLoading, launchError } = useSelector(
    (state) => state.game
  );

  const frameWrapRef = useRef(null);
  const iframeRef = useRef(null);
  const [frameReady, setFrameReady] = useState(false);

  const invalidGame = !sports && !/^\d+$/.test(prdId || "");

  const startLaunch = useCallback(() => {
    setFrameReady(false);
    if (sports) {
      dispatch(launchSports());
      return;
    }
    dispatch(
      launchGame({
        prdId,
        gameId,
        tableId,
        category,
        isMobile,
        homeUrl: window.location.origin,
      })
    );
  }, [dispatch, sports, prdId, gameId, tableId, category, isMobile]);

  useEffect(() => {
    if (user.userLoggedIn && !invalidGame) startLaunch();
    return () => {
      dispatch(resetLaunch());
    };
  }, [user.userLoggedIn, invalidGame, startLaunch, dispatch]);

  // the launcher owns the whole area below the header, so hide the ticker
  useEffect(() => {
    const headline = document.getElementById("headlines");
    if (headline) headline.style.display = "none";
    return () => {
      if (headline) headline.style.display = "";
    };
  }, []);

  // size the frame to the space between the header and the viewport bottom
  useEffect(() => {
    const resize = () => {
      const wrap = frameWrapRef.current;
      if (!wrap) return;
      const top = wrap.getBoundingClientRect().top + window.scrollY;
      wrap.style.height = `${Math.max(window.innerHeight - top, 400)}px`;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [launchUrl]);

  const goFullscreen = () => {
    const frame = iframeRef.current;
    if (!frame) return;
    const request =
      frame.requestFullscreen ||
      frame.webkitRequestFullscreen ||
      frame.mozRequestFullScreen;
    if (request) request.call(frame);
  };

  if (invalidGame) {
    return (
      <div className="game-launch game-launch--message">
        <i className="bi bi-controller"></i>
        <p>This game link is no longer valid.</p>
        <button className="standard-btn" onClick={() => navigate("/all-games")}>
          Browse Games
        </button>
      </div>
    );
  }

  if (launchError) {
    return (
      <div className="game-launch game-launch--message">
        <i className="bi bi-exclamation-triangle"></i>
        <p>{launchError}</p>
        <div className="game-launch-actions">
          <button className="standard-btn" onClick={startLaunch}>
            Try Again
          </button>
          <button
            className="standard-btn game-launch-back"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (launchLoading || !launchUrl) {
    return (
      <div className="game-launch game-launch--message">
        <img loading="lazy" src={loadingGif} width={120} height={120} alt="loading" />
        <p>Launching {gameName || "game"}…</p>
      </div>
    );
  }

  return (
    <div className="game-launch" ref={frameWrapRef}>
      {!frameReady && (
        <div className="game-launch-overlay">
          <img loading="lazy" src={loadingGif} width={120} height={120} alt="loading" />
          <p>Loading {gameName || "game"}…</p>
        </div>
      )}
      <iframe
        ref={iframeRef}
        id="gameLaunchFrame"
        src={launchUrl}
        title={gameName || "Game"}
        allow="autoplay; fullscreen; clipboard-write"
        allowFullScreen
        onLoad={() => setFrameReady(true)}
      ></iframe>
      {!isMobile && frameReady && (
        <button
          className="game-launch-fullscreen"
          onClick={goFullscreen}
          title="Fullscreen"
        >
          <i className="bi bi-arrows-fullscreen"></i>
        </button>
      )}
    </div>
  );
}
